import React, { useState } from 'react';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import { ChevronRight } from 'lucide-react';
import { MovemberBanner, movemberActive } from '../components/MovemberBanner';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('register');
  const [movember, setMovember] = useState(movemberActive);

  // Scroll to top when navigating
  const handleNavigation = (path) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  const featureCards = [
    {
      title: "Public Speaking",
      subtitle: "The courses advance effective communication and cultivate the craft of composing and delivering speeches and presentations.",
      image: "https://static.prod-images.emergentagent.com/jobs/4df90070-0088-49f2-8c24-6ddbc348eaae/images/7d9875000ac8443883c51d33c4767093cfb1fd787f33f7f96f3b17f47e5981e9.jpeg",
      link: "/public-speaking"
    },
    {
      title: "Courses",
      subtitle: "Explore multiple free courses designed to sharpen your critical thinking, with new courses added every month.",
      image: "https://static.prod-images.emergentagent.com/jobs/4df90070-0088-49f2-8c24-6ddbc348eaae/images/7418e65fe12677acbd2b077c7c2da3c7612bc0ec674637e91694998a4491d42d.jpeg",
      link: "/modules"
    },
    {
      title: "Articles",
      subtitle: "Read our articles and essays on thinking, language and ideas, published regularly on the BWGELA blog.",
      image: "https://static.prod-images.emergentagent.com/jobs/29ef0ecf-33aa-4e02-a294-016f03b70dc8/images/e1e7afc2779cacf30bd202d2d6fcdba59e1470a07dc343ddc14428e2a2d355f6.jpeg",
      link: "/blog"
    },
    {
      title: "Biases",
      subtitle: "Discover and understand the cognitive biases that quietly influence your thinking and decision making every day.",
      image: "https://static.prod-images.emergentagent.com/jobs/4df90070-0088-49f2-8c24-6ddbc348eaae/images/8618521d338509e3a4dba8fc2751828f558d29f37673ef7ca6169a70205c0039.jpeg",
      link: "/biases"
    },
    {
      title: "Psychology Modules",
      subtitle: "Read free, bite-sized psychology modules across cognition, development and social life, each ending with critical thinking questions to ponder.",
      image: "https://static.prod-images.emergentagent.com/jobs/74430a05-50f7-47b9-938b-3b2a7245b74a/images/5bd2139af2ca23410fcca85c7cf133b13709dcd82a8392cb9ded3834ae2d53b7.jpeg",
      link: "/books"
    },
    {
      title: "Rhetoric",
      subtitle: "Master the art of persuasion through the study of language, logic and the power of well crafted arguments.",
      image: "https://static.prod-images.emergentagent.com/jobs/4df90070-0088-49f2-8c24-6ddbc348eaae/images/cb1227be1661c78038ee5c16628d165b313844b4b53a04df3002467d25a1628d.jpeg",
      link: "/rhetoric"
    }
  ];

  const handleCardClick = () => {};

  return (
    <>
    {movember && <MovemberBanner onClose={() => setMovember(false)} />}
    <div className={`bg-[#0a0a0a] ${movember ? 'pt-14 sm:pt-16 lg:pt-20' : 'pt-[120px] sm:pt-[140px] lg:pt-[160px]'} pb-24 sm:pb-28 lg:pb-32 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-6xl mx-auto">
        
        {/* Title */}
        <div className="text-center mb-20 sm:mb-24">
          <p className="text-sm tracking-[0.3em] text-gray-400 mb-2" data-testid="home-est-year">Since 1999</p>
          <h1 className="text-3xl md:text-4xl text-[#F5F5DC] mb-4">
            BWGELA
          </h1>
          <p className="text-base text-white">
            Critical Thinking - Public Speaking - Trivium
          </p>
          <p className="text-base text-gray-400 italic mt-3 max-w-xl mx-auto leading-relaxed" data-testid="home-tagline">
            &ldquo;Pause the algorithm, start a conversation&rdquo;
          </p>
          <p className="text-base text-white mt-1 max-w-xl mx-auto leading-relaxed">
            A free/donation based platform
          </p>
        </div>

        {/* Feature Grid - same layout as the Blog (3 x 2 on large screens) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featureCards.map((card, index) => (
            <div
              key={index}
              onClick={() => handleNavigation(card.link)}
              className="group bg-[#1a1a1a] rounded-xl overflow-hidden border border-[#333] hover:border-[#444] transition-all cursor-pointer"
              data-testid={`home-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <h3 className="text-[#F5F5DC] text-lg mb-2 group-hover:text-white transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                  {card.subtitle}
                </p>
                <div className="flex items-center justify-end text-sm">
                  <span className="text-[#F5F5DC] flex items-center gap-1 group-hover:gap-2 transition-all">
                    Explore <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 sm:mt-28">
          <NewsletterSignup source="homepage" />
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
        initialMode={authMode}
      />
    </div>
    </>
  );
};

export default HomePage;
